import { createBrowserRouter, useNavigate, useParams } from "react-router";
import { DashboardKine } from "./pages/DashboardKine";
import { PrescribirRutina } from "./pages/PrescribirRutina";
import { DetallePaciente } from "./pages/DetallePaciente";
import { HomePaciente } from "./pages/HomePaciente";
import { SesionCamara } from "./pages/SesionCamara";
import { Onboarding } from "./pages/Onboarding";
import { ProgresoPaciente } from "./pages/ProgresoPaciente";
import { SesionCompletada } from "./pages/SesionCompletada";
import { MensajesPaciente } from "./pages/MensajesPaciente";
import { PerfilPaciente } from "./pages/PerfilPaciente";
import { BibliotecaEjercicios } from "./pages/BibliotecaEjercicios";
import { RutinasKine } from "./pages/RutinasKine";
import { CalendarioKine } from "./pages/CalendarioKine";
import { MensajesKine } from "./pages/MensajesKine";
import { ConfiguracionKine } from "./pages/ConfiguracionKine";
import { NotificacionesPaciente } from "./pages/NotificacionesPaciente";
import { PrivacidadPaciente } from "./pages/PrivacidadPaciente";
import { AyudaPaciente } from "./pages/AyudaPaciente";
import { ProfesionalesKine } from "./pages/ProfesionalesKine";
import { SesionesKine } from "./pages/SesionesKine";
import { SesionKineDetalle } from "./pages/SesionKineDetalle";
import { TestPose } from "./pages/TestPose";
import { DevDbTest } from "./pages/DevDbTest";
import { HistorialSesiones } from "./pages/HistorialSesiones";
import { Login } from "./pages/Login";
import { ForgotPassword } from "./pages/ForgotPassword";
import { ResetPassword } from "./pages/ResetPassword";
import { SignupKine } from "./pages/SignupKine";
import { SignupPaciente } from "./pages/SignupPaciente";
import { SignupEmailEnviado } from "./pages/SignupEmailEnviado";
import { SignupPacienteError } from "./pages/SignupPacienteError";
import { AuthCallback } from "./pages/AuthCallback";
import { useEjerciciosDelDia } from "@/lib/supabase/queries/useEjerciciosDelDia";
import { useCrearSesion } from "@/lib/supabase/queries/useCrearSesion";
import { useSesion } from "@/lib/supabase/queries/useSesion";
import { useSkipEjercicio } from "@/lib/supabase/queries/useSkipEjercicio";
import { useUltimoMensajeKine } from "@/lib/supabase/queries/useUltimoMensajeKine";
import { useProfilePaciente } from "@/lib/supabase/queries/useProfilePaciente";
import { useCurrentPacienteId } from "@/app/components/auth/AuthProvider";
import { useProgresoPaciente } from "@/lib/paciente/useProgresoPaciente";
import { useEvolucionPaciente } from "@/lib/paciente/useEvolucionPaciente";
import { getExerciseSchema } from "@/lib/pose/exerciseSchemas";
import { formatRelativeDate } from "@/lib/utils";
import { VideoOnlyView } from "./components/sessions/VideoOnlyView";

function SesionCamaraWrapper() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const sesion = useSesion(sessionId);

  if (sesion.loading) {
    return <FullScreenMessage title="Cargando sesión…" />;
  }

  if (sesion.errorKind === "not-found") {
    return (
      <FullScreenMessage
        title="Sesión no encontrada"
        body="El link no apunta a una sesión existente. Volvé al inicio para empezar una nueva."
        onVolver={() => navigate("/paciente-home")}
      />
    );
  }

  if (sesion.errorKind === "fetch-error") {
    return (
      <FullScreenMessage
        title="No pudimos cargar la sesión"
        body={sesion.errorMessage ?? "Probá de nuevo en unos segundos."}
        onVolver={() => navigate("/paciente-home")}
      />
    );
  }

  // sesion.data garantizada por exhaustividad de los branches anteriores.
  const data = sesion.data!;

  if (data.estado !== "en_progreso") {
    return (
      <FullScreenMessage
        title="Esta sesión ya fue completada"
        body="No se puede reabrir una sesión cerrada. Volvé al inicio para empezar una nueva."
        onVolver={() => navigate("/paciente-home")}
      />
    );
  }

  const schema = getExerciseSchema(data.ejercicio_id);
  if (!schema) {
    return (
      <FullScreenMessage
        title="Ejercicio no soportado"
        body={`El ejercicio "${data.ejercicio_id}" no tiene un schema cargado en la app. Avisale a tu kine.`}
        onVolver={() => navigate("/paciente-home")}
      />
    );
  }

  // Branch tracking_mode: schemas video-only (ej: mov_cuello) van a la vista
  // de video embebido + textarea, SIN cámara ni MediaPipe ni evaluator.
  // El branch va acá (antes de SesionCamara) para que los hooks pesados de
  // SesionCamara (useMediaPipePose, createEvaluator) NO se ejecuten en el
  // flow video_only.
  if (schema.tracking_mode === "video_only") {
    return <VideoOnlyView sessionId={data.id} schema={schema} />;
  }

  return <SesionCamara sessionId={data.id} exerciseId={data.ejercicio_id} />;
}

function PacienteHomeWrapper() {
  const navigate = useNavigate();
  const { data: ejercicios, error, loading, refetch } = useEjerciciosDelDia();
  const crearSesion = useCrearSesion();
  const skipEjercicio = useSkipEjercicio();
  const pacienteId = useCurrentPacienteId();
  const ultimoMsgKine = useUltimoMensajeKine(pacienteId);
  // Sprint 3 fix: nombre real del paciente vía useProfilePaciente. Antes
  // vivía hardcoded como "Paciente Demo" → HomePaciente saludaba
  // "Hola, Paciente". Fallback string "Paciente" mientras el hook
  // resuelve o si hay error — evita bloquear todo el screen por el
  // fetch del profile secundario.
  const { data: profilePaciente } = useProfilePaciente();
  const pacienteNombre = profilePaciente?.paciente.nombre ?? "Paciente";
  // Nombre del kine: null si RLS no deja leer `kines` desde el paciente.
  const kineNombre = profilePaciente?.kine?.nombre ?? "Tu kinesiólogo";
  const kineIniciales = profilePaciente?.kine?.nombre
    ? profilePaciente.kine.nombre
        .split(" ")
        .map((w) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "K";
  const progresoPaciente = useProgresoPaciente();
  const evolucion = useEvolucionPaciente(pacienteId);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Cargando tu rutina…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="text-center max-w-sm">
          <p className="text-red-600 mb-2">No pudimos cargar tu rutina del día.</p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  // Click handler: lookup del plan_ejercicio_id en la lista que ya tenemos,
  // INSERT en `sesiones` via el hook, navega con el id nuevo. Doble click
  // queda guardado por el `loading` del hook (no spawn una segunda call
  // mientras hay una en vuelo).
  const handleEmpezar = async (ejercicioId: string) => {
    if (crearSesion.loading) return;
    const ej = ejercicios?.find((e) => e.id === ejercicioId);
    if (!ej) {
      console.error("[PacienteHomeWrapper] ejercicio no encontrado en la lista", ejercicioId);
      return;
    }
    const sessionId = await crearSesion.crear({
      planEjercicioId: ej.planEjercicioId,
      ejercicioId: ej.id,
    });
    if (sessionId) {
      navigate(`/sesion/${sessionId}`);
    }
    // Si crear() devolvió null, el banner de error abajo lo muestra.
  };

  // DEV-only: skip button para QA. Crea sesión completada stub y refetchea
  // la lista para que el "actual" avance al próximo ejercicio. Producción
  // recibe undefined → HomePaciente no renderiza el botón.
  const handleSkip = import.meta.env.DEV
    ? async (planEjercicioId: string, ejercicioId: string) => {
        if (skipEjercicio.loading) return;
        const ok = await skipEjercicio.skip({ planEjercicioId, ejercicioId });
        if (ok) {
          await refetch();
        }
      }
    : undefined;

  return (
    <>
      {crearSesion.error && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-red-50 border border-red-200 rounded-lg px-4 py-2 shadow-lg max-w-sm">
          <p className="text-sm text-red-700">
            No pudimos arrancar la sesión: {crearSesion.error.message}
          </p>
        </div>
      )}
      {skipEjercicio.error && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-red-50 border border-red-200 rounded-lg px-4 py-2 shadow-lg max-w-sm">
          <p className="text-sm text-red-700">
            No pudimos saltar el ejercicio: {skipEjercicio.error.message}
          </p>
        </div>
      )}
      <HomePaciente
        paciente={{ nombre: pacienteNombre }}
        rutinaHoy={{ ejercicios: ejercicios ?? [] }}
        progresoSemana={{
          completados: progresoPaciente.sesionesSemana,
          rachaDias: progresoPaciente.rachaDias,
        }}
        progreso={{
          adherenciaPct: progresoPaciente.adherencia?.adherencia_pct ?? null,
          rachaDias: progresoPaciente.rachaDias,
          rom: evolucion.data?.rom ?? null,
        }}
        mensajeKine={
          ultimoMsgKine
            ? {
                texto: ultimoMsgKine.content,
                fecha: formatRelativeDate(new Date(ultimoMsgKine.created_at)),
              }
            : undefined
        }
        onEmpezarEjercicio={handleEmpezar}
        onAbrirMensajes={() => navigate('/mensajes')}
        kineIniciales={kineIniciales}
        kineNombre={kineNombre}
        onSkipEjercicio={handleSkip}
        skipLoading={skipEjercicio.loading}
      />
    </>
  );
}

/** Pantalla full screen con título + cuerpo opcional + botón "Volver al inicio".
 *  Usado por SesionCamaraWrapper para los distintos estados de error y por
 *  el spinner inicial. */
function FullScreenMessage({
  title,
  body,
  onVolver,
}: {
  title: string;
  body?: string;
  onVolver?: () => void;
}) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="text-center max-w-sm">
        <h1 className="text-2xl font-semibold mb-2">{title}</h1>
        {body && (
          <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
            {body}
          </p>
        )}
        {onVolver && (
          <button
            onClick={onVolver}
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold text-sm px-5 py-3 hover:bg-primary/90 transition-colors"
          >
            Volver al inicio
          </button>
        )}
      </div>
    </div>
  );
}

export const router = createBrowserRouter([
  {
    path: "/",
    Component: DashboardKine,
  },
  {
    path: "/prescribir/:patientId",
    Component: PrescribirRutina,
  },
  {
    path: "/paciente/:patientId",
    Component: DetallePaciente,
  },
  {
    path: "/paciente-home",
    Component: PacienteHomeWrapper,
  },
  {
    path: "/sesion/:sessionId",
    Component: SesionCamaraWrapper,
  },
  {
    path: "/sesion-completada/:sessionId",
    Component: SesionCompletada,
  },
  {
    path: "/onboarding",
    Component: Onboarding,
  },
  {
    path: "/progreso",
    Component: ProgresoPaciente,
  },
  {
    path: "/historial",
    Component: HistorialSesiones,
  },
  {
    path: "/mensajes",
    Component: MensajesPaciente,
  },
  {
    path: "/perfil",
    Component: PerfilPaciente,
  },
  {
    path: "/biblioteca",
    Component: BibliotecaEjercicios,
  },
  {
    path: "/rutinas",
    Component: RutinasKine,
  },
  {
    path: "/calendario",
    Component: CalendarioKine,
  },
  {
    path: "/mensajes-kine",
    Component: MensajesKine,
  },
  {
    path: "/configuracion",
    Component: ConfiguracionKine,
  },
  {
    path: "/notificaciones",
    Component: NotificacionesPaciente,
  },
  {
    path: "/privacidad",
    Component: PrivacidadPaciente,
  },
  {
    path: "/ayuda",
    Component: AyudaPaciente,
  },
  {
    path: "/profesionales",
    Component: ProfesionalesKine,
  },
  {
    path: "/sesiones",
    Component: SesionesKine,
  },
  {
    path: "/sesion-kine/:sessionId",
    Component: SesionKineDetalle,
  },
  {
    path: "/test-pose",
    Component: TestPose,
  },
  {
    path: "/dev/db-test",
    Component: DevDbTest,
  },
  {
    path: "/login",
    Component: Login,
  },
  {
    path: "/forgot-password",
    Component: ForgotPassword,
  },
  {
    path: "/reset-password",
    Component: ResetPassword,
  },
  {
    path: "/signup-kine",
    Component: SignupKine,
  },
  {
    path: "/signup-paciente",
    Component: SignupPaciente,
  },
  {
    path: "/signup-kine/email-enviado",
    Component: SignupEmailEnviado,
  },
  {
    path: "/signup-paciente/email-enviado",
    Component: SignupEmailEnviado,
  },
  {
    path: "/signup-paciente/error",
    Component: SignupPacienteError,
  },
  {
    path: "/auth/callback",
    Component: AuthCallback,
  },
]);
